import {
  type LoginInput,
  type StaffRole,
  type StaffSessionDto,
  staffRoleSchema,
  staffSessionDto,
} from '@planuze/admin-contracts';
import { z } from 'zod';
import { ApiError, type ApiErrorCode, parseJson, readErrorCode, request } from './http.js';

/* Cliente REST de autenticação de staff (ADR-0354). Núcleo HTTP em `http.ts`. */

export { ApiError } from './http.js';

const HTTP_UNAUTHORIZED = 401;
const HTTP_TOO_MANY = 429;

/* A resposta do login é dona do admin-service (`{ ok, role }`); validamos contra
   este mirror local — o teste de contrato falha se o serviço divergir. */
const staffLoginResponseSchema = z.object({
  ok: z.literal(true),
  role: staffRoleSchema,
});

/** POST /v1/staff/login — sucesso seta o cookie e devolve a role; falha lança ApiError. */
export async function login(input: LoginInput): Promise<StaffRole> {
  const res = await request('/v1/staff/login', { method: 'POST', body: input });
  if (res.ok) {
    return (await parseJson(res, staffLoginResponseSchema)).role;
  }
  if (res.status === HTTP_TOO_MANY) {
    const code = await readErrorCode(res);
    const mapped: ApiErrorCode = code === 'account_locked' ? 'account_locked' : 'rate_limited';
    throw new ApiError(res.status, mapped, 'too many attempts');
  }
  throw new ApiError(res.status, 'invalid_credentials', 'invalid credentials');
}

/** POST /v1/staff/logout — limpa o cookie no servidor; ignora o corpo. */
export async function logout(): Promise<void> {
  await request('/v1/staff/logout', { method: 'POST' });
}

/** GET /v1/staff/me — `null` quando não autenticado (401), senão a sessão validada. */
export async function fetchStaffSession(): Promise<StaffSessionDto | null> {
  const res = await request('/v1/staff/me');
  if (res.status === HTTP_UNAUTHORIZED) {
    return null;
  }
  if (!res.ok) {
    throw new ApiError(res.status, 'unknown', 'failed to load session');
  }
  return parseJson(res, staffSessionDto);
}
